import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Card, CardContent } from "@/shared/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Building2, Globe, MessageSquare } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { WeComUnifiedConfig } from "./WeComUnifiedConfig";
import { WebEntriesTab } from "./web-entry/WebEntriesTab";
import { WeComWelcomeCard } from "./WeComWelcomeCard";
import { pickChannelTab, type ChannelTab } from "./channelTab";
import { useWeComModuleBound } from "@/entities/wecom-app/model/useWeComModuleBound";

/**
 * 渠道绑定管理 —— 只看自家租户的渠道(租户管理员 / 经理,或平台超管代客进来)。
 *
 * 修复波 0926 第八批 Z1:删掉了「平台超管跨租户列表(master)→ ?tenantId= 详情」两段式视图。
 * 非代客平台超管访问 /channels 已被路由 deny(ProtectedRoute roleRouteDeny.PLATFORM_ADMIN),
 * 代客态 effectiveRole = TENANT_ADMIN,那条分支早已不可达;平台看租户渠道走租户详情页。
 */
export default function ChannelManagement() {
  const { t } = useTranslation("platform");
  const { user } = useAuth();
  const { isImpersonating, meta: impMeta } = useImpersonation();

  // 租户管理员 → 自己的 tenant;代客平台超管 → impersonation meta 的 tenantId
  // (JWT 不带 tenantCode/Id,必须从代客上下文拿)
  const ownTenantId = user?.tenantId ?? (isImpersonating ? impMeta?.tenantId : null) ?? null;
  const ownTenantCode =
    user?.tenantCode ?? (isImpersonating ? impMeta?.tenantCode : undefined) ?? undefined;

  if (ownTenantId === null) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          {t("channelMgmt.noTenantInfo")}
        </CardContent>
      </Card>
    );
  }
  return (
    <ChannelDetailView
      tenant={{
        id: ownTenantId,
        code: ownTenantCode ?? "",
        name: user?.tenantName ?? ownTenantCode ?? t("channelMgmt.myTenant"),
      }}
    />
  );
}

// ── Detail 视图 ─────────────────────────────────────────────────────────

function ChannelDetailView({ tenant }: { tenant: { id: number; code: string; name: string } }) {
  const { t } = useTranslation("platform");
  // `/channels?tab=web|wecom` 直达对应页签(帮助文档 / 引导卡 / 客服舱提示可深链)
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const wecomBound = useWeComModuleBound("customer_service");
  const [tab, setTab] = useState<ChannelTab>(() => pickChannelTab(tabParam, wecomBound));
  // 用户没手动切过时,企微绑定状态查回来 / URL 参数变了 → 跟着改默认页签
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) setTab(pickChannelTab(tabParam, wecomBound));
  }, [tabParam, wecomBound, touched]);
  return (
    <div className="space-y-5">
      {/* Header — 租户上下文 */}
      <div className="flex items-start gap-3 fx-enter">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-xl font-bold text-foreground truncate">
              {tenant.name || tenant.code || "—"}
            </h1>
            {tenant.code && (
              <span className="text-xs font-mono text-muted-foreground">{tenant.code}</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{t("channelMgmt.detailDescription")}</p>
        </div>
      </div>

      {/* Channel-type tabs */}
      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTouched(true);
          setTab(v as ChannelTab);
        }}
      >
        <TabsList>
          <TabsTrigger value="wecom" className="gap-1.5">
            <MessageSquare className="h-4 w-4" />
            {t("channelMgmt.tabWecom")}
          </TabsTrigger>
          {/* 1010 客服 H5 端:网页 / H5 接入点(web_widget 渠道),与企微客服同属客服舱 */}
          <TabsTrigger value="web" className="gap-1.5" data-testid="channel-tab-web">
            <Globe className="h-4 w-4" />
            {t("channelMgmt.tabWebH5")}
          </TabsTrigger>
          {/* 钉钉 / 飞书 tab 在加 provider 时再插 */}
        </TabsList>

        <TabsContent value="wecom" className="mt-4">
          <Card>
            <CardContent className="p-4">
              <WeComUnifiedConfig tenantId={tenant.id} />
            </CardContent>
          </Card>
          {/* 1010 一期补完契约 5:企微客服欢迎语写入口(网页 / H5 接入点欢迎语留空时也回落到它) */}
          <WeComWelcomeCard className="mt-4" />
        </TabsContent>

        <TabsContent value="web" className="mt-4">
          <WebEntriesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
